import type {
  Action,
  ActionContext,
  StepResult,
  ActionHandler,
  ActionWithContext,
} from './types';
import { ActionResult } from './enums';
import { prepareActionWithContext } from './utils/prepare-action-with-context';
import { ActionBus } from './ActionBus';

import { localStorageSetHandler } from './defaultActions/localStorageSet';
import { sessionStorageSetHandler } from './defaultActions/sessionStorageSet';
import { finishHookHandler, goHomeHandler } from './defaultActions/finishHook';
import { sendCloudEvent } from './defaultActions/sendCloudEvent';
import { refreshComponent } from './defaultActions/refreshComponent';
import { confirmDialogHandler } from './defaultActions/confirmDialog';
import { pinCodeAction } from '~/services/plugins/parent-lock/pin-flows.ts';
import { registerQueueActions } from '~/services/queue';
import { showConfirmationToastHandler } from './defaultActions/showConfirmationToast';
import { toggleFavoriteAction } from './defaultActions/toggleFavorite';
import logger from '~/services/logger';

/**
 * Registers and executes action handlers via an in-process bus.
 * Supports fire-and-forget dispatching and sequential execution with status events.
 *
 * @fires `<type>_success`
 * @fires `<type>_error`
 * @fires `<type>_cancel`
 */
export class ActionRegistry {
  private static instance: ActionRegistry | null = null;
  private handlers = new Map<string, Set<ActionHandler>>();
  private bus: ActionBus;
  private ctx: ActionContext;

  private constructor(bus: ActionBus) {
    this.bus = bus;
    this.ctx = {
      env: 'browser',
      data: {},
      location: undefined,
      signal: undefined,
    } as ActionContext;

    this.bus.on((action) => void this.handle(action));
    this.dispatch = this.dispatch.bind(this);

    /* Registering built-in default handlers */
    this.register('localStorageSet', localStorageSetHandler);
    this.register('sessionStorageSet', sessionStorageSetHandler);
    this.register('finishHook', finishHookHandler);
    this.register('goHome', goHomeHandler);
    this.register('sendCloudEvent', sendCloudEvent);
    this.register('postForm', sendCloudEvent);
    this.register('refreshComponent', refreshComponent);
    this.register('pinCode', pinCodeAction);
    this.register('showConfirmationToast', showConfirmationToastHandler);
    this.register('confirmDialog', confirmDialogHandler);
    this.register('toggleFavorite', toggleFavoriteAction);

    registerQueueActions({ register: this.register.bind(this) });
  }

  static getInstance(): ActionRegistry {
    if (!ActionRegistry.instance) {
      ActionRegistry.instance = new ActionRegistry(ActionBus.getInstance());
    }

    return ActionRegistry.instance;
  }

  updateContext(ctx: Partial<ActionContext>): void {
    this.ctx = { ...this.ctx, ...ctx };
  }

  /**
   * Registers a handler for an action `type`.
   *
   * @param type - Action type key.
   * @param handler - Handler to invoke for the type.
   * @returns Unregister function.
   */
  register(type: string, handler: ActionHandler) {
    const set = this.handlers.get(type) ?? new Set<ActionHandler>();
    set.add(handler);
    this.handlers.set(type, set);
    return () => set.delete(handler);
  }

  /**
   * Dispatches an action via the bus without emitting status events.
   *
   * @param action - Action to dispatch.
   */
  dispatch(action: Action) {
    this.bus.emit(action);
  }

  /**
   * Executes actions sequentially and emits per-step status events.
   *
   * @param actions - Actions to run sequentially.
   * @param opts - Optional overrides.
   * @param opts.signal - Abort signal to cancel execution.
   * @param opts.context - Extra context merged for this run.
   * @returns Final result and per-step outcomes.
   */
  async execute(
    actions: Action[],
    opts?: { signal?: AbortSignal; context?: Record<string, unknown> }
  ): Promise<{ final: ActionResult; steps: StepResult[] }> {
    const steps: StepResult[] = [];

    for (let index = 0; index < actions.length; index += 1) {
      const action = actions[index];
      // external cancel (or inherited cancel)
      const signal = opts?.signal ?? this.ctx.signal;
      if (signal?.aborted) {
        const step = { action, result: ActionResult.Cancel as const };
        steps.push(step);
        this.emitStatus(action, ActionResult.Cancel);
        return { final: ActionResult.Cancel, steps };
      }

      const step = await this.runOne(
        {
          ...action,
          context: {
            ...(opts?.context ?? {}),
            hasFollowingActions: index < actions.length - 1,
          },
        },
        { signal }
      );
      steps.push(step);

      // stop immediately on cancel
      if (step.result === ActionResult.Cancel) {
        return { final: ActionResult.Cancel, steps };
      }
    }

    // if any error happened but no cancel, you can choose final semantics.
    // Here: Error if any step errored, otherwise Success.
    const final = steps.some((s) => s.result === ActionResult.Error)
      ? ActionResult.Error
      : ActionResult.Success;

    return { final, steps };
  }

  private async runOne(
    action: ActionWithContext,
    override?: { signal?: AbortSignal }
  ): Promise<StepResult> {
    /**
     * Executes a single action and emits its status event.
     *
     * @param action - Action (with context) to run.
     * @param override - Optional override with `signal` for cancellation.
     * @returns Per-step result.
     * @private
     */
    try {
      const res = await this.handleWithResult(action, override);
      this.emitStatus(action, res);
      return { action, result: res };
    } catch (error) {
      this.emitStatus(action, ActionResult.Error, error);
      return { action, result: ActionResult.Error, error };
    }
  }

  /**
   * Emits a status event for an action step.
   *
   * @param action - Action for this step.
   * @param result - Step result.
   * @param error - Optional error when `result` is `Error`.
   * @private
   */
  private emitStatus(action: Action, result: ActionResult, error?: unknown) {
    const suffix =
      result === ActionResult.Success
        ? 'success'
        : result === ActionResult.Error
        ? 'error'
        : 'cancel';

    if (suffix === 'error') {
      logger.error(
        `ActionRegistry: Action type "${action.type}" resulted in error. ${
          (error as { message?: string })?.message ?? error
        }`
      );
    }

    this.bus.emit({
      type: `${action.type}_${suffix}`,
      options: { action, error },
    });
  }

  /**
   * Handles an action without emitting status events.
   *
   * @param action - Prepared action to handle.
   * @private
   */
  private async handle(action: ActionWithContext) {
    await this.handleWithResult(action);
  }

  /**
   * Runs all handlers for a type.
   * - If any handler returns Cancel => Cancel
   * - Else if any handler throws => Error (caught by caller)
   * - Else Success
   * If a handler returns void => treated as Success
   */
  private async handleWithResult(
    action: ActionWithContext,
    override?: { signal?: AbortSignal }
  ): Promise<ActionResult> {
    /**
     * Runs handlers for an action type and computes the result.
     *
     * @param action - Action with context for handlers.
     * @param override - Optional override with `signal` for cancellation checks.
     * @returns Final `ActionResult` for this action.
     * @private
     */
    const set = this.handlers.get(action.type);
    if (!set || set.size === 0) return ActionResult.Success;

    const ctx: ActionContext = { ...this.ctx, ...override };

    for (const handler of set) {
      if (ctx.signal?.aborted) return ActionResult.Cancel;

      const preparedAction = prepareActionWithContext(action, ctx);

      const out = await handler(preparedAction as any, ctx);

      if (out === ActionResult.Cancel) return ActionResult.Cancel;
      if (out === ActionResult.Error) return ActionResult.Error;
      // void or Success => continue
    }

    return ActionResult.Success;
  }
}
